import { LOCK_PRESETS } from "../../config/supernovaXp";
import { SUPERNOVA_TOKEN_IDS } from "./supernovaContracts";

/**
 * supernovaWriteArgs — everything that turns what a member picked on screen into
 * the exact arguments the staking contract takes, plus the one place that
 * decides whether this build has a verified contract interface to write against
 * at all.
 *
 * PURE AND DEPENDENCY-FREE. No viem, no React, no network, no signing. It maps
 * and it validates; supernovaChain does the talking.
 *
 * EVERY SIGNATURE HERE COMES FROM SUPERNOVA_ABI_FACTS.md. Nothing is inferred,
 * nothing is guessed, and the fragments actually used are read out of
 * src/config/supernovaStakingAbi.json rather than written down a second time:
 *
 *   stake(address token, uint256 tokenId, uint256 amount, uint256 duration)
 *   unstake(uint256 stakeId)
 *   emergencyUnstake(uint256 stakeId)
 *
 * THE TWO UNITS THAT MATTER (both verified against real transactions):
 *   duration  SECONDS. 86400 = 1 day, and minStakeDuration() on the live
 *             contract is 86400. The member picks days; this converts.
 *   amount    a RAW ERC-1155 quantity — 3 means three NFTs. It is NOT an
 *             18-decimal token amount, and treating it as one would ask a
 *             member to sign a stake of 3×10^18 NFTs they do not have.
 *
 * NULL MEANS "WE CAN'T MAP THAT". Every builder returns null rather than a
 * fallback value or a thrown string, so a caller can never accidentally send a
 * coerced 0 to a contract. supernovaChain turns a null into a typed, member-safe
 * error before anything reaches a wallet.
 */

/* BigInt is ES2020 and every browser in our browserslist has it; the CRA eslint
   env doesn't declare it (same note as supernovaChain.js). */
/* global BigInt */

/** Verified: `duration` is seconds, and the dApp computes days × 24 × 60 × 60. */
export const SECONDS_PER_DAY = 86400;

/** minStakeDuration() read from the live contract at head. */
export const MIN_STAKE_DURATION_SECONDS = 86400;

const isWholeCount = (n) => typeof n === "number" && Number.isInteger(n) && n >= 0;

/* ── Duration: the member's days → the contract's seconds ───────────────── */

/**
 * Map a chosen lock to the contract's `duration` argument.
 *
 * Presets only, deliberately: the XP model rates a lock by the tier table, so a
 * duration outside it would be a lock we can price on-chain and cannot rate in
 * the app. The stake modal offers nothing else.
 *
 * @param {number} lockDays one of LOCK_PRESETS
 * @returns {bigint|null} seconds, or null when it isn't a lock we offer
 */
export function stakeDurationSeconds(lockDays) {
  if (!Number.isInteger(lockDays) || lockDays < 1) return null;
  if (!LOCK_PRESETS.some((preset) => preset.days === lockDays)) return null;
  const seconds = lockDays * SECONDS_PER_DAY;
  /* Every preset clears the floor by a wide margin; this is here so a future
     preset shorter than a day can't quietly become a reverting signature. */
  if (seconds < MIN_STAKE_DURATION_SECONDS) return null;
  return BigInt(seconds);
}

/** The days a contract `duration` represents — the inverse, for reading back. */
export function lockDaysFromDurationSeconds(duration) {
  const seconds =
    typeof duration === "bigint" ? Number(duration) : Number(duration);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds % SECONDS_PER_DAY !== 0) return null;
  return seconds / SECONDS_PER_DAY;
}

/* ── Quantity and token id ──────────────────────────────────────────────── */

/**
 * The `amount` argument: a raw count of NFTs, never a decimal-scaled amount.
 * @returns {bigint|null}
 */
export function stakeAmountArg(qty) {
  if (!isWholeCount(qty) || qty < 1) return null;
  return BigInt(qty);
}

/**
 * The `tokenId` argument, restricted to the four Supernova ids. An id outside
 * the collection is a bug on our side, not a stake we should offer to sign.
 * @returns {bigint|null}
 */
export function stakeTokenIdArg(tokenId) {
  const id = typeof tokenId === "bigint" ? Number(tokenId) : tokenId;
  if (!SUPERNOVA_TOKEN_IDS.includes(id)) return null;
  return BigInt(id);
}

/* ── Stake id ───────────────────────────────────────────────────────────── */

/**
 * The `stakeId` both exit functions take. Prefer an explicit stakeId; fall back
 * to the lot id, which the read layer is specified to build as the contract's
 * own stake id (optionally prefixed, e.g. "42161:0xabc:3").
 *
 * TODO(backend, supervised): this is the coupling between the read layer and
 * the exit writes. When SUPERNOVA_STAKING_READ lands it MUST carry the
 * contract's stakeId — either as `stakeId` on the lot or as the last segment of
 * `lotId`. Anything else is refused here rather than turned into a signature
 * over somebody else's position.
 *
 * @param {string|number|bigint|null|undefined} value
 * @returns {bigint|null}
 */
export function stakeIdArg(value) {
  if (typeof value === "bigint") return value >= BigInt(0) ? value : null;
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 ? BigInt(value) : null;
  }
  if (typeof value !== "string" || value.trim() === "") return null;
  const last = value.trim().split(":").pop();
  return /^\d+$/.test(last) ? BigInt(last) : null;
}

/* ── The verified ABI, or nothing at all ────────────────────────────────── */

/**
 * The three member-facing writes, by canonical type list. A fragment that
 * doesn't match this exactly is not the function we verified, so it isn't used.
 */
const REQUIRED_WRITES = {
  stake: ["address", "uint256", "uint256", "uint256"],
  unstake: ["uint256"],
  emergencyUnstake: ["uint256"],
};

const matchesFragment = (fragment, name, types) =>
  fragment &&
  fragment.type === "function" &&
  fragment.name === name &&
  Array.isArray(fragment.inputs) &&
  fragment.inputs.length === types.length &&
  fragment.inputs.every((input, i) => input && input.type === types[i]);

/**
 * Pick the three verified write fragments out of a full ABI.
 *
 * Returns null — never a partial list — if any one of them is missing or has a
 * different shape than the one SUPERNOVA_ABI_FACTS.md verified. A half-known
 * interface is treated exactly like no interface: the app falls back to saying
 * signing is unavailable rather than signing against a guess.
 *
 * @param {unknown} abi parsed contents of supernovaStakingAbi.json
 * @returns {Array<object>|null}
 */
export function selectStakingWriteFragments(abi) {
  if (!Array.isArray(abi)) return null;
  const picked = [];
  for (const [name, types] of Object.entries(REQUIRED_WRITES)) {
    const fragment = abi.find((f) => matchesFragment(f, name, types));
    if (!fragment) return null;
    picked.push(fragment);
  }
  return picked;
}

let cachedWriteAbi;

/**
 * The staking write ABI for this build, or null if it isn't there.
 *
 * The artifact is loaded defensively on purpose. It is the single thing that
 * decides whether this app can offer to sign a stake at all, so "the file isn't
 * there / isn't what we verified" has to be a runtime answer the UI can render,
 * not an exception on the way in.
 *
 * @returns {Array<object>|null}
 */
export function getStakingWriteAbi() {
  if (cachedWriteAbi === undefined) {
    let raw = null;
    try {
      // eslint-disable-next-line global-require
      raw = require("../../config/supernovaStakingAbi.json");
    } catch (e) {
      raw = null;
    }
    cachedWriteAbi = selectStakingWriteFragments(raw);
  }
  return cachedWriteAbi;
}

/** Test seam — drop the memoized ABI lookup. */
export function resetStakingWriteAbi() {
  cachedWriteAbi = undefined;
}
